import type { DOMOutputSpec, Mark, MarkSpec } from 'prosemirror-model';

import type { RtSchemaType } from '../../editor/types';
import { MarkName } from '../../schema/marks';
import type { ProsemirrorAttrs } from '../../schema/schema';

export type HighlightAttrs = {
  hex: string;
};

const rgbRegex = /^rgb\((\d+),(\d+),(\d+)\)$/;

function isHexColor(hexString: string) {
  const hexRegex = /^#[0-9A-F]{6}$/i;
  return hexRegex.test(hexString);
}

function rgbToHex(rgbString: string) {
  const rgb = rgbString.replaceAll(' ', '');
  const match = rgbRegex.exec(rgb);

  if (!match) {
    return null;
  }
  const red = parseInt(match[1], 10);
  const green = parseInt(match[2], 10);
  const blue = parseInt(match[3], 10);

  // Convert components to hex (with padding)
  const hex =
    '#' +
    red.toString(16).padStart(2, '0') +
    green.toString(16).padStart(2, '0') +
    blue.toString(16).padStart(2, '0');

  return hex.toUpperCase();
}

// ColorMark can parse both hex and rgb() values but outputs only hex
export class HighlightMark implements MarkSpec {
  public static readonly markName = MarkName.BackgroundColor;
  public attrs: ProsemirrorAttrs<HighlightAttrs> = {
    hex: {},
  };
  public parseDOM = [
    {
      tag: 'span',
      getAttrs: (dom: HTMLElement): HighlightAttrs | false => {
        const color = dom.style.backgroundColor;
        if (!color) return false;
        const hexColor = isHexColor(color) ? color : rgbToHex(color);
        return hexColor
          ? {
              hex: hexColor,
            }
          : false;
      },
    },
  ];
  public toDOM(mark: Mark): DOMOutputSpec {
    const { hex } = <HighlightAttrs>mark.attrs;
    return ['span', { style: `background-color:${hex}` }];
  }
}

export type HighlightSchema = RtSchemaType<any, typeof HighlightMark>;
